import { MSB_DEFAULT_LOGO, S3 } from "../constants";
import i18n from "../i18n";

const Chat = (app) => {
  app.post(
    "/chat/group-chat-save",
    async ({ req, res, flash, api, assert, locals }) => {
      const { body, files = null } = req;
      const { participants, subject, allGroupChats, isAutoManaged } = body;
      const { activeUserToRequest } = locals;
      const groupChatsNames = allGroupChats ? JSON.parse(allGroupChats) : [];
      const isSubjectIncluded = groupChatsNames.includes(subject);
      const isAutoManagedBool = !!isAutoManaged;

      if (!isAutoManagedBool) {
        assert(
          participants,
          400,
          i18n.t("coaching-portal:chatActions.provideParticipants")
        );
      }
      assert(
        subject,
        400,
        i18n.t("coaching-portal:chatActions.provideSubject")
      );
      assert(
        !isSubjectIncluded,
        400,
        i18n.t("coaching-portal:chatActions.groupChatNameExists")
      );

      delete body.allGroupChats;

      let bodyToSend = { subject };

      if (isAutoManagedBool) {
        bodyToSend.isAutoManaged = true;
      } else {
        const participantsArray = JSON.parse(participants);
        bodyToSend.participants = participantsArray.map(
          ({ _id, name, avatar }) => ({
            _id,
            name,
            ...(!!avatar && { avatar }),
          })
        );
      }

      // Always send a fallback: the API prefers the uploaded image and only
      // falls back to this, so a dropped upload can't leave photoUrl unset.
      const organizationLogo = activeUserToRequest.avatar?.s3Path
        ? `${S3.PATH}${activeUserToRequest.avatar.s3Path}`
        : MSB_DEFAULT_LOGO;
      bodyToSend = { ...bodyToSend, organizationLogo };

      try {
        await api(`create-group-chat`, {
          method: "POST",
          body: bodyToSend,
          files,
        });

        flash(
          "success",
          i18n.t("coaching-portal:chatActions.chatGroupCreatedSuccessfully")
        );
      } catch (e) {
        flash("error", e.message);
      }
      res.redirect("/coaching-portal/chat");
    }
  );

  // Fire-and-forget from the chat box's onSendMessage: TalkJS delivers messages
  // straight from the browser, so this is how msb-api learns a coach messaged a
  // client (feeds the dashboard's "Chat Message" column).
  const parseJsonBody = (body) => {
    if (typeof body !== "string") return body || {};
    try {
      return JSON.parse(body);
    } catch {
      return {};
    }
  };

  app.post("/chat/coach-activity", async ({ req, res, api }) => {
    try {
      await api("coach-chat-activity", {
        method: "POST",
        body: { athleteId: parseJsonBody(req.body).athleteId },
      });
    } catch (e) {
      console.error("coach-chat-activity failed", e);
    }
    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    res.status = 200;
    res.body = JSON.stringify({ success: true });
  });

  // Fetch-driven (not a native <form> submit) — MassMessageModal carries a recorded
  // audio Blob it can't put through a form field, so it POSTs FormData via fetch()
  // and expects a small JSON status back instead of a redirect. `flash()` still runs
  // so the toast shows once the modal closes and triggers the usual /actions/refresh.
  app.post("/chat/mass-message", async ({ req, res, flash, api, assert }) => {
    const { body, files = null } = req;
    const { text = "", voiceDuration } = body;

    let recipients;
    try {
      recipients = JSON.parse(body.recipients || "[]");
    } catch {
      recipients = [];
    }

    assert(
      Array.isArray(recipients) && recipients.length > 0,
      400,
      i18n.t("coaching-portal:chatActions.provideParticipants")
    );

    let success = true;
    let error;

    try {
      // recipients goes as a JSON string, not a plain array: RillFetcher flattens array
      // body fields into bracket-notation form fields (recipients[0], recipients[1], ...)
      // and msb-api's body parser does not reconstruct those back into an array.
      await api("send-mass-message", {
        method: "POST",
        body: { recipients: JSON.stringify(recipients), text, voiceDuration },
        files,
      });
      flash(
        "success",
        i18n.t("coaching-portal:chatActions.massMessageSentSuccessfully")
      );
    } catch (e) {
      success = false;
      // Only forward e.message when it's an HttpError raised from msb-api's own sanitized
      // X-Error-Message header (js/middleware/init.js's api.on("response") handler) — that
      // text is guaranteed sanitized and user-safe. Anything else (network failure, a
      // RillFetcher-level exception, etc.) isn't meant for a user to see.
      error =
        e.name === "HttpError"
          ? e.message
          : i18n.t("coaching-portal:chatActions.massMessageSendError");
      if (e.name !== "HttpError") console.error(e);
      flash("error", error);
    }

    res.headers = res.headers || {};
    res.headers["Content-Type"] = "application/json; charset=utf-8";
    res.status = success ? 200 : 400;
    res.body = JSON.stringify(success ? { success } : { success, error });
  });

  app.post(
    "/chat/group-chat-remove-members",
    async ({ req, res, flash, api, assert }) => {
      const { body } = req;
      const { participants, conversation } = body;

      assert(
        participants,
        400,
        i18n.t("coaching-portal:chatActions.provideParticipants")
      );
      assert(
        conversation,
        400,
        i18n.t("coaching-portal:chatActions.provideConversation")
      );

      try {
        await api(`remove-members-group-chat`, {
          method: "POST",
          body: {
            participants,
            conversation,
          },
        });

        flash(
          "success",
          i18n.t("coaching-portal:chatActions.membersDeletedSuccessfully")
        );
      } catch (e) {
        flash("error", e.message);
      }

      res.refreshWith(
        {
          modal: "",
          groupParticipants: "",
          groupSubject: "",
          groupChatId: "",
          groupImage: "",
          groupEdited: true,
          allGroupChats: "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  app.post(
    "/chat/group-chat-add-members",
    async ({ req, res, flash, api, assert }) => {
      const { body } = req;
      const { participants, conversation } = body;

      assert(
        participants,
        400,
        i18n.t("coaching-portal:chatActions.provideParticipants")
      );
      assert(
        conversation,
        400,
        i18n.t("coaching-portal:chatActions.provideConversation")
      );

      const simplifiedParticipants = participants.map(({ _id, name }) => ({
        _id,
        name,
      }));

      try {
        await api(`add-members-group-chat`, {
          method: "POST",
          body: {
            participants: simplifiedParticipants,
            conversation,
          },
        });

        flash(
          "success",
          i18n.t("coaching-portal:chatActions.membersAddedSuccessfully")
        );
      } catch (e) {
        flash("error", e.message);
      }
      res.refreshWith(
        {
          modal: "",
          groupParticipants: "",
          groupSubject: "",
          groupChatId: "",
          groupImage: "",
          groupEdited: true,
          allGroupChats: "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  app.post(
    "/chat/group-chat-edit",
    async ({ req, res, flash, api, assert }) => {
      const { body, files = null } = req;
      const { subject, allGroupChats, conversationId } = body;
      //Converting the string to an array
      const groupChatsArray = allGroupChats ? JSON.parse(allGroupChats) : [];
      //Building an object array, before de @ are the ids, and after are the subjects
      const groupChats = groupChatsArray.map((chat) => {
        const [id, subject] = chat.split("@");
        return { id, subject };
      });

      const isSubjectBodyValid = !groupChats.some(
        (chat) => chat.subject === subject && chat.id !== conversationId
      );

      assert(
        subject,
        400,
        i18n.t("coaching-portal:chatActions.provideSubject")
      );
      assert(
        conversationId,
        400,
        i18n.t("coaching-portal:chatActions.provideConversation")
      );
      assert(
        isSubjectBodyValid,
        400,
        i18n.t("coaching-portal:chatActions.groupChatNameExists")
      );

      delete body.allGroupChats;

      try {
        await api(`edit-group-chat`, {
          method: "POST",
          body,
          files,
        });

        flash(
          "success",
          i18n.t("coaching-portal:chatActions.chatGroupCreatedSuccessfully")
        );
      } catch (e) {
        flash("error", e.message);
      }
      res.refreshWith(
        {
          modal: "",
          groupParticipants: "",
          groupSubject: "",
          groupChatId: "",
          groupImage: "",
          groupEdited: true,
          allGroupChats: "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );
};

export default Chat;
